import { IBrandDetails } from '@/common/types';
import * as Sentry from '@sentry/browser';

const Brand = {
	getBrand: (local = false): string => {
		// Strip the TLD out of a url...
		let brand: string = 'constellation';
		try {
			const hostName: string = window.location.href.match(
				/^(?:https?:\/\/)?(?:[^@\/\n]+@)?(?:www\.)?([^:\/\n]+)/im,
			)[1];
			if (hostName.includes('.')) {
				const domains: string[] = hostName.split('.');
				brand = domains[domains.length - 2];

				if (local && domains[domains.length - 3] === 'local') {
					return 'local';
				}
			}
		} catch (e) {
			Sentry.captureException(e);
		}

		if (brand === 'cloudfront') {
			brand = 'umia';
		}

		if (brand === 'mmicgroup') {
			brand = 'mmic';
		}

		if (brand === 'localhost') {
			brand = 'insurecareteams'; // Default for testing
		}

		if (brand === 'starlabs') {
			brand = 'umia';
		}

		if (brand === 'arkansasmutual') {
			brand = 'amic';
		}

		if (brand === 'mmicrrg') {
			brand = 'rrg';
		}

		//console.log(`Detected: '${brand}'...`);
		return brand;
	},
	getProperBrand: (brand = Brand.getBrand()) => {
		return Brand.getBrandInformation(brand).proper_name;
	},
	getBrandBillPay: (brand = Brand.getBrand()) => {
		if (Brand.getBrandInformation(brand).proper_name === "MMIC RRG") {
			return "MMIC Risk Retention Group, Inc";
		} else {
			return `${Brand.getBrandInformation(brand).proper_name} Insurance, Inc`;
		}
	},
	getBrandInformation: (brand: string = Brand.getBrand()): IBrandDetails => {
		let brandDetails: IBrandDetails;

		switch (brand) {
			case 'umia':
			default:
				brandDetails = {
					claims_email: 'Claims@UMIA.com',
					domain: 'CONSTELLATIONMUTUAL.com',
					email_address: 'support@constellationmutual.com',
					phone_number: '1.800.328.5532',
					proper_name: 'UMIA',
					url: 'https://myaccount.umia.com/'
				};
				break;
			
			case 'rrg':
				brandDetails = {
					claims_email: 'support@constellationmutual.com',
					domain: 'mmicrrg.com',          // 'rrg.test.constellationmutual.net',
					email_address: 'support@constellationmutual.com',
					phone_number: '1.800.328.5532',
					proper_name: 'MMIC RRG',	// WAS RRG
					url: 'https://mmicrrg.com/'     // 'https://rrg.test.constellationmutual.net/'
				};
				break;

			case 'insurecareteams':
				brandDetails = {
					claims_email: 'support@constellationmutual.com',
					domain: 'InsureCAREteams.com',
					email_address: 'support@constellationmutual.com',
					phone_number: '1.800.328.5532',
					proper_name: 'InsureCAREteams',
					url: 'https://myaccount.insurecareteams.com/'
				};
				break;

			case 'mmic':
				brandDetails = {
					claims_email: 'Claim.Mail@constellationmutual.com',
					domain: 'CONSTELLATIONMUTUAL.com',
					email_address: 'support@constellationmutual.com',
					phone_number: '1.800.328.5532',
					proper_name: 'MMIC',
					url: 'https://myaccount.mmicgroup.com/'
				};
				break;

			case 'arkansas':
			case 'amic':
				brandDetails = {
					claims_email: 'support@constellationmutual.com',
					domain: 'CONSTELLATIONMUTUAL.com',
					email_address: 'support@constellationmutual.com',
					phone_number: '1.800.328.5532',
					proper_name: 'Arkansas Mutual',
					url: 'https://myaccount.arkansasmutual.com/'
				};
				break;
		}

		return brandDetails;
	},
	getBrandUrl(brand: string = Brand.getBrand() ): string {
		return Brand.getBrandInformation( brand ).url;
	},
	getClaimsEmail(format: 'display' | 'href', brand: string = Brand.getBrand()): string {
		const claimsEmail: string = Brand.getBrandInformation(brand).claims_email;
		switch (format) {
			case 'display':
				return claimsEmail;

			case 'href':
				return `mailto:${claimsEmail}`;

			default:
				throw new Error(`Unhandled claims email \`format\` value: '${format}'`);
		}
	},
	getSupportEmail(format: 'display' | 'href', brand: string = Brand.getBrand()): string {
		const supportEmail: string = Brand.getBrandInformation(brand).email_address;
		switch (format) {
			case 'display':
				return supportEmail;

			case 'href':
				return `mailto:${supportEmail}`;

			default:
				throw new Error(`Unhandled support email \`format\` value: '${format}'`);
		}
	},
	getPhoneNumber(format: 'dashed' | 'dotted' | 'href', brand: string = Brand.getBrand()): string {
		const phoneNumber: string = Brand.getBrandInformation(brand).phone_number;

		// This isn't so much a pattern for separators as it is a pattern for any sequence of one
		// or more non-digits, which would account for the possibility of something like
		// '+1 (555) 555-5555'.
		const separatorRegex: RegExp = /[^\d]+/g;

		switch (format) {
			case 'dashed':
				return phoneNumber.replace(separatorRegex, '-');

			case 'dotted':
				return phoneNumber.replace(separatorRegex, '.');

			case 'href':
				return `tel:${phoneNumber.replace(separatorRegex, '')}`;

			default:
				throw new Error(`Unhandled phone number \`format\` value: '${format}'`);
		}
	},
	getBrandsObject() {
		return {
			umia: 'UMIA',
			mmic: 'MMIC',
			amic: 'Arkansas Mutual',
			insurecareteams: 'InsureCAREteams',
			rrg : 'RRG'
		};
	},
	setMetaTitle: () => {
		return Brand.getProperBrand();
	},
	setFavicon: () => {
		const link = document.createElement('link');
		link.rel = 'shortcut icon';
		link.href = Brand.getBrand() + '-favicon.ico';

		document.head.appendChild(link);
	},
	getClientID: (): string => {
		return process.env?.['VUE_APP_CLIENT_ID_' + Brand.getBrand().toUpperCase()];
	},
	getStripeKey: (): string => {
		return process.env?.['VUE_APP_STRIPE_KEY_' + Brand.getBrand().toUpperCase()];
	},
	getOneTrustSrc: (): string => {
		return process.env?.['VUE_APP_ONE_TRUST_' + Brand.getBrand().toUpperCase()];
	},
	getGtmId: (): string => {
		const brand = Brand.getBrand();
		switch (brand) {
			case 'umia':
				return process.env?.VUE_APP_GTM_ID_UMIA;
			case 'amic':
				return process.env?.VUE_APP_GTM_ID_AMIC;
			case 'rrg':
				return process.env?.VUE_APP_GTM_ID_RRG;
			case 'mmic':
			default:
				return process.env?.VUE_APP_GTM_ID_MMIC;
		}
	},
};

export default Brand;
